import { LitElement, html, css } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { Firestore } from 'firebase/firestore';
import { 
  getWorkOrdersByCreatedBy,
  getSafetyReportsByCompany,
  WorkOrder
} from '@cmms/api';
import { WorkOrderStatusCat, WorkOrderPriorityCat } from '@cmms/models';



interface SafetyReport {
  id: string;
  model?: {
    title: string;
    status: string;
    date_reported: number;
  };
}

@customElement('shop-floor-dashboard')
export class ShopFloorDashboard extends LitElement {
  static styles = css`
    :host {
      display: block;
      padding: 1.5rem;
    }

    .welcome-section {
      background: linear-gradient(135deg, var(--sl-color-primary-600) 0%, var(--sl-color-primary-800) 100%);
      color: white;
      padding: 2rem;
      border-radius: var(--sl-border-radius-large);
      margin-bottom: 2rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .welcome-section h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.75rem;
      font-weight: 600;
    }

    .welcome-section p {
      margin: 0;
      opacity: 0.9;
    }

    .quick-actions {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .action-card {
      background: white;
      border: 2px solid var(--sl-color-neutral-200);
      border-radius: var(--sl-border-radius-medium);
      padding: 1.5rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .action-card:hover {
      border-color: var(--sl-color-primary-600);
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .action-card.primary {
      background: linear-gradient(135deg, var(--sl-color-danger-600) 0%, var(--sl-color-danger-800) 100%);
      color: white;
      border-color: var(--sl-color-danger-600);
    }

    .action-card.primary:hover {
      border-color: var(--sl-color-danger-700);
    }

    .action-card .icon {
      font-size: 2.5rem;
      margin-bottom: 0.5rem;
      display: block;
    }

    .action-card h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1.1rem;
      font-weight: 600;
    }

    .action-card p {
      margin: 0;
      font-size: 0.875rem;
      opacity: 0.8;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: white;
      border-radius: var(--sl-border-radius-medium);
      padding: 1.5rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      border-left: 4px solid var(--sl-color-primary-500);
    }

    .stat-card .label {
      font-size: 0.875rem;
      color: var(--sl-color-neutral-600);
      margin-bottom: 0.5rem;
    }

    .stat-card .value {
      font-size: 2rem;
      font-weight: 600;
      color: var(--sl-color-neutral-900);
    }

    .stat-card.warning { border-left-color: var(--sl-color-warning-500); }
    .stat-card.success { border-left-color: var(--sl-color-success-500); }

    .reports-section {
      background: white;
      border-radius: var(--sl-border-radius-medium);
      padding: 1.5rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      margin-bottom: 1.5rem;
    }

    .reports-section h2 {
      margin: 0 0 1rem 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--sl-color-neutral-900);
    }

    .report-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .report-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
      background: var(--sl-color-neutral-50);
      border-radius: var(--sl-border-radius-medium);
      border-left: 3px solid var(--sl-color-primary-500);
    }

    .report-item .info {
      flex: 1;
    }

    .report-item .title {
      font-weight: 600;
      margin-bottom: 0.25rem;
      color: var(--sl-color-neutral-900);
    }

    .report-item .time {
      font-size: 0.875rem;
      color: var(--sl-color-neutral-600);
    }

    .badge {
      padding: 0.25rem 0.75rem;
      border-radius: var(--sl-border-radius-pill);
      font-size: 0.75rem;
      font-weight: 600;
    }

    .badge.open {
      background: var(--sl-color-warning-100);
      color: var(--sl-color-warning-700);
    }

    .badge.completed {
      background: var(--sl-color-success-100);
      color: var(--sl-color-success-700);
    }

    sl-spinner {
      display: block;
      margin: 2rem auto;
    }

    .empty-state {
      text-align: center;
      padding: 2rem;
      color: var(--sl-color-neutral-600);
    }

    .empty-state sl-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      opacity: 0.3;
    }
  `;

  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  @property({ type: Object }) user: any = null;

  @state() private isLoading = true;
  @state() private myWorkOrders: WorkOrder[] = [];
  @state() private mySafetyReports: any[] = [];
  @state() private stats = {
    myOpenReports: 0,
    myOpenWorkOrders: 0
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.companyId) {
      this.loadDashboardData();
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('companyId') && this.companyId) {
      this.loadDashboardData();
    }
  }

  private async loadDashboardData() {
    if (!this.firestore || !this.companyId) return;

    this.isLoading = true;
    try {
      await Promise.all([
        this.loadWorkOrders(),
        this.loadReports()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadWorkOrders() {
    try {
      const result = await getWorkOrdersByCreatedBy(this.firestore, this.companyId, this.user?.uid || '');
      
      if (result.data && result.data.length > 0) {
        // Already filtered by the API, just sort and limit
        const myOrders = [...result.data];
        
        // Sort by created date, most recent first
        myOrders.sort((a: any, b: any) => {
          const dateA = a.model?.created_at || a.created_at || 0;
          const dateB = b.model?.created_at || b.created_at || 0;
          return dateB - dateA;
        });
        
        // Show last 5
        this.myWorkOrders = myOrders.slice(0, 5) as WorkOrder[];
        this.stats.myOpenWorkOrders = myOrders.filter((wo: any) => 
          wo.model?.status_cat === WorkOrderStatusCat.OPEN ||
          wo.model?.status_cat === WorkOrderStatusCat.IN_PROGRESS
        ).length;
      } else {
        this.myWorkOrders = [];
        this.stats.myOpenWorkOrders = 0;
      }
    } catch (error) {
      console.error('Error loading work orders:', error);
    }
  }

  private async loadReports() {
    try {
      const result = await getSafetyReportsByCompany(this.firestore, this.companyId);
      if (result.data) {
        // Filter reports created by current user
        const myReports = result.data
          .filter((report: any) => 
            report.model?.reported_by_user_id === this.user?.uid || 
            report.reported_by_user_id === this.user?.uid
          );
        // Sort by date reported, most recent first
        myReports.sort((a: any, b: any) => {
          const dateA = a.model?.date_reported || 0;
          const dateB = b.model?.date_reported || 0;
          return dateB - dateA;
        });
        // Show last 5
        this.mySafetyReports = myReports.slice(0, 5);
        this.stats.myOpenReports = myReports.filter((report: any) => 
          report.model?.status?.toLowerCase() === 'open' || 
          report.model?.status?.toLowerCase() === 'pending'
        ).length;
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  }

  private handleCreateWorkOrder() {
    const modal = document.createElement('work-order-modal') as any;
    modal.open = true;
    modal.companyId = this.companyId;
    modal.firestore = this.firestore;
    modal.userRole = this.user?.role || ''; // Pass user role
    modal.formData = {
      description: '',
      asset_id: '',
      priority_cat: WorkOrderPriorityCat.MEDIUM,
      status_cat: WorkOrderStatusCat.OPEN,
      assigned_to: ''
    };
    
    document.body.appendChild(modal);
    
    modal.addEventListener('modal-closed', () => {
      document.body.removeChild(modal);
      this.loadDashboardData(); // Reload data after adding work order
    }, { once: true });
  }

  private handleReportIncident() {
    const modal = document.createElement('safety-report-modal') as any;
    modal.open = true;
    modal.companyId = this.companyId;
    modal.firestore = this.firestore;
    
    document.body.appendChild(modal);
    
    modal.addEventListener('safety-report-saved', () => {
      this.loadDashboardData(); // Reload data after adding safety report
    });
    
    // Auto-close the modal when closed
    modal.addEventListener('modal-closed', () => {
      document.body.removeChild(modal);
    }, { once: true });
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hrs ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  }

  private getStatusBadge(status: string) {
    if (status.toLowerCase().includes('open') || status.toLowerCase().includes('pending')) {
      return 'open';
    }
    if (status.toLowerCase().includes('completed') || status.toLowerCase().includes('resolved')) {
      return 'completed';
    }
    return 'open';
  }

  private getWorkOrderStatusBadge(statusCat: number) {
    switch (statusCat) {
      case WorkOrderStatusCat.OPEN:
        return 'open';
      case WorkOrderStatusCat.IN_PROGRESS:
        return 'open';
      case WorkOrderStatusCat.COMPLETED:
        return 'completed';
      default:
        return 'open';
    }
  }

  private getWorkOrderStatusLabel(statusCat: number) {
    switch (statusCat) {
      case WorkOrderStatusCat.OPEN:
        return 'Open';
      case WorkOrderStatusCat.IN_PROGRESS:
        return 'In Progress';
      case WorkOrderStatusCat.COMPLETED:
        return 'Completed';
      default:
        return 'Open';
    }
  }



  render() {
    if (this.isLoading) {
      return html`<sl-spinner size="large"></sl-spinner>`;
    }

    const userName = this.user?.displayName || this.user?.name || 'Team Member';

    return html`
      <div class="welcome-section">
        <h1>Welcome back, ${userName}! 👋</h1>
        <p>Quick access to report safety incidents and maintenance issues</p>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <div class="action-card primary" @click=${this.handleReportIncident}>
          <span class="icon">⚠️</span>
          <h3>Report Incident</h3>
          <p>File a safety report</p>
        </div>
        <div class="action-card" @click=${this.handleCreateWorkOrder}>
          <span class="icon">📋</span>
          <h3>Create Work Order</h3>
          <p>Report a maintenance issue</p>
        </div>
      </div>

      <!-- Stats -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="label">My Open Reports</div>
          <div class="value">${this.stats.myOpenReports}</div>
        </div>
        <div class="stat-card warning">
          <div class="label">My Open Work Orders</div>
          <div class="value">${this.stats.myOpenWorkOrders}</div>
        </div>
      </div>

      <!-- My Recent Work Orders -->
      <div class="reports-section">
        <h2>📋 My Recent Work Orders</h2>
        ${this.myWorkOrders.length > 0 ? html`
          <div class="report-list">
            ${this.myWorkOrders.map((wo: any) => html`
              <div class="report-item">
                <div class="info">
                  <div class="title">${wo.model?.title || wo.model?.description || 'No title'}</div>
                  <div class="time">${this.formatDate(wo.model?.created_at || Date.now())}</div>
                </div>
                <span class="badge ${this.getWorkOrderStatusBadge(wo.model?.status_cat)}">
                  ${this.getWorkOrderStatusLabel(wo.model?.status_cat)}
                </span>
              </div>
            `)}
          </div>
        ` : html`
          <div class="empty-state">
            <sl-icon name="clipboard-check"></sl-icon>
            <p>No work orders yet</p>
          </div>
        `}
      </div>

      <!-- My Recent Reports -->
      <div class="reports-section">
        <h2>⚠️ My Recent Safety Reports</h2>
        ${this.mySafetyReports.length > 0 ? html`
          <div class="report-list">
            ${this.mySafetyReports.map((report: any) => html`
              <div class="report-item">
                <div class="info">
                  <div class="title">${report.model?.title || 'No title'}</div>
                  <div class="time">${this.formatDate(report.model?.date_reported || Date.now())}</div>
                </div>
                <span class="badge ${this.getStatusBadge(report.model?.status || 'open')}">
                  ${report.model?.status || 'Open'}
                </span>
              </div>
            `)}
          </div>
        ` : html`
          <div class="empty-state">
            <sl-icon name="shield-exclamation"></sl-icon>
            <p>No safety reports yet</p>
          </div>
        `}
      </div>
    `;
  }
}
